import { getAddress, isAddress, type Address } from "viem";
import { z } from "zod";
import { ApiClient, ApiError, ApiSessionChangedError } from "../api/api-client";
import { abortable } from "../api/abortable";
import { DEFAULT_SOURCE_CHAIN_ID } from "../config/chains";
import { currentChainId, injectedProvider, requestAccount, signSiweMessage, type Eip1193Provider } from "./eip1193";

const nonceResponseSchema = z.object({
  nonce: z.string(),
  message: z.string().min(1),
  issuedAt: z.number().int(),
  expiresAt: z.number().int(),
  domain: z.string(),
  uri: z.string(),
  chainId: z.number().int().positive(),
});

const verifyResponseSchema = z.object({
  csrfToken: z.string().min(1),
  walletAddress: z.string(),
  issuedAt: z.number().int(),
  expiresAt: z.number().int(),
});

const sessionResponseSchema = z.object({
  walletAddress: z.string().refine(isAddress).transform(value => getAddress(value)),
  authExpiresAt: z.number().int().positive(),
  serverTime: z.number().int().nonnegative(),
  chainId: z.number().int().positive(),
  csrfToken: z.string().min(1),
});
type CookieSession = Readonly<z.infer<typeof sessionResponseSchema> & {
  observedAt: number; monotonicAt: number; requestDuration: number;
}>;
export type AuthState = "checking" | "unavailable" | "unauthenticated" | "authenticated" |
  "waiting-wallet" | "configuration-error" | "logout-pending" | "logout-failed";

export type WalletSessionSnapshot = Readonly<{
  authState: AuthState;
  sessionRevision: number;
  providerAvailable: boolean;
  account: Address | null;
  chainId: number | null;
  connected: boolean;
  authenticated: boolean;
  sessionExpiresAt: number | null;
  status: "DISCONNECTED" | "CONNECTING" | "SIGNING_IN" | "CONNECTED" | "AUTHENTICATED" | "WRONG_CHAIN" | "SESSION_EXPIRED" | "ERROR";
  error: string | null;
}>;

const STORAGE_KEY = "greenwood.wallet-session.v1";

export class WalletSessionController {
  private disposed = false;
  private started = false;
  private revision = 0;
  private identityRevision = 0;
  private provider: Eip1193Provider | null = null;
  private account: Address | null = null;
  private chainId: number | null = null;
  private session: CookieSession | null = null;
  private authState: AuthState = "checking";
  private checkGeneration = 0;
  private checkRetainsAuthentication = false;
  private checking: { generation: number; promise: Promise<void>; abort: AbortController } | null = null;
  private logoutPending: Promise<void> | null = null;
  private logoutBlocked = false;
  private channel: BroadcastChannel | null = null;
  private lastFocusCheck = Number.NEGATIVE_INFINITY;
  private error: string | null = null;
  private activity: "CONNECTING" | "SIGNING_IN" | null = null;
  private pending: Promise<void> | null = null;
  private actionAbort: AbortController | null = null;
  private sessionExpired = false;
  private expiryTimer: ReturnType<typeof setTimeout> | null = null;
  private listeners = new Set<() => void>();
  private snapshotCache: WalletSessionSnapshot | null = null;
  private expectedSiweDomain: string | null = null;
  private expectedSiweUri: string | null = null;
  private siweRevision = 0;
  private connectionKey: string | null = null;
  private connectorAvailable: boolean | null = null;

  readonly api = new ApiClient({
    baseUrl: import.meta.env.VITE_API_BASE_URL?.trim() || "/api",
    getSessionRevision: () => { this.validSession(); return this.revision; },
    getCsrfToken: () => this.session?.csrfToken ?? null,
    canRequestProtected: () => !this.logoutBlocked && this.validSession() !== null,
    onCsrfFailure: code => {
      if (code === "CSRF_TOKEN_INVALID") {
        this.invalidateCheck();
        this.checkRetainsAuthentication = false;
        this.authState = "checking";
        this.emit();
        void this.checkSession().catch(() => { /* Retry is exposed in authState. */ });
      } else {
        this.authState = "configuration-error";
        this.error = "Cookie authentication was blocked by the server origin policy. Check configuration and retry.";
        this.session = null;
        this.emit();
      }
    },
    onUnauthorized: () => this.expireSession("Session is no longer valid. Sign in again."),
  });

  constructor(private expectedSourceChainId = DEFAULT_SOURCE_CHAIN_ID, private readonly now = () => Date.now()) {
    // Remove only the retired SIWE record; connector and recovery owners are separate.
    try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* Cookie auth does not require Web Storage. */ }
  }

  /** Called by the effect-owned runtime, never from a render or passive wallet event. */
  start(): void {
    if (this.disposed || this.started) return;
    this.started = true;
    if (typeof BroadcastChannel !== "undefined") {
      try {
        this.channel = new BroadcastChannel("merklewood.cookie-auth.v1");
        this.channel.onmessage = event => { if (event.data === "auth-changed") this.onAuthChange(); };
      } catch { /* Restricted browsers use a storage event containing only a signal. */ }
    }
    window.addEventListener("storage", this.onStorage);
    window.addEventListener("focus", this.onResume);
    window.addEventListener("pageshow", this.onResume);
    document.addEventListener("visibilitychange", this.onResume);
    void this.checkSession().catch(() => { /* State owns retry. */ });
  }

  private readonly onResume = () => {
    if (document.visibilityState === "hidden" || this.disposed || this.logoutBlocked) return;
    // Coalesce focus/pageshow/visibility events; no recurring session polling.
    if (this.now() - this.lastFocusCheck < 1_000) return;
    this.lastFocusCheck = this.now();
    void this.checkSession().catch(() => { /* State owns retry. */ });
  };

  private readonly onAuthChange = () => {
    if (this.disposed) return;
    this.cancelAction();
    this.invalidateCheck();
    if (!this.logoutBlocked) this.session = null;
    this.authState = this.logoutBlocked ? this.logoutPending ? "logout-pending" : "logout-failed" : "checking";
    this.emit();
    if (!this.logoutBlocked) void this.checkSession().catch(() => { /* State owns retry. */ });
  };

  private readonly onStorage = (event: StorageEvent) => {
    if (event.key === "merklewood.auth-change" && event.newValue) this.onAuthChange();
  };

  private signalChange(): void {
    if (!this.started) return;
    if (this.channel) this.channel.postMessage("auth-changed");
    else {
      try { localStorage.setItem("merklewood.auth-change", crypto.randomUUID()); }
      catch { /* Focus/visibility revalidation still works if messaging/storage is unavailable. */ }
    }
  }


  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): WalletSessionSnapshot => {
    if (this.snapshotCache && (!this.snapshotCache.authenticated || this.session && this.remainingMs(this.session) > 0)) return this.snapshotCache;
    const authenticated = this.validSession() !== null;
    const wrongChain = this.connected && this.chainId !== this.expectedSourceChainId;
    this.snapshotCache = Object.freeze({
      authState: this.authState,
      sessionRevision: this.revision,
      providerAvailable: this.provider !== null || (this.connectorAvailable ?? injectedProvider() !== null),
      account: this.account,
      chainId: this.chainId,
      connected: this.connected,
      authenticated,
      sessionExpiresAt: this.session?.authExpiresAt ?? null,
      status: this.activity ?? (wrongChain ? "WRONG_CHAIN" : authenticated ? "AUTHENTICATED" :
        this.connected && this.sessionExpired ? "SESSION_EXPIRED" : this.error ? "ERROR" :
        this.connected ? "CONNECTED" : "DISCONNECTED"),
      error: this.error,
    });
    return this.snapshotCache;
  };

  get connected(): boolean { return this.account !== null; }
  getProvider(): Eip1193Provider | null { return this.provider; }
  getAccount(): Address | null { return this.account; }
  getRevision(): number { return this.revision; }
  /** Reuse the controller's existing anchored deadline for informational UI. */
  sessionRemainingMs(): number { const session = this.validSession(); return session ? Math.max(0, this.remainingMs(session)) : 0; }
  getIdentityRevision(): number { return this.identityRevision; }
  getSiweRevision(): number { return this.siweRevision; }

  setConnectorAvailability(available: boolean): void {
    if (this.disposed || this.connectorAvailable === available) return;
    this.connectorAvailable = available;
    this.emit(false);
  }

  /** Adopt connector state without prompting or attaching a second set of wallet listeners. */
  syncConnection(connection: { provider: Eip1193Provider; account: Address; chainId: number; key: string }): void {
    if (this.disposed) return;
    if (!Number.isSafeInteger(connection.chainId) || connection.chainId <= 0) {
      this.invalidateConnection();
      throw new Error("Wallet returned an invalid chain ID");
    }
    const account = getAddress(connection.account);
    const identityChanged = this.provider !== connection.provider || this.account !== account || this.connectionKey !== connection.key;
    const chainChanged = this.chainId !== connection.chainId;
    if (!identityChanged && !chainChanged) return;
    this.cancelAction();
    this.detach();
    if (identityChanged) this.identityRevision++;
    this.invalidateCheck();
    if (!this.logoutBlocked) this.session = null;
    this.provider = connection.provider;
    this.account = account;
    this.chainId = connection.chainId;
    this.connectionKey = connection.key;
    this.error = null;
    this.emit();
    this.recheckContext();
  }

  /** Fence old writes immediately while a different connector's provider resolves. */
  invalidateConnection(): void {
    if (this.disposed || this.connectionKey === null) return;
    this.disconnect();
  }

  setConnectionProgress(pending: boolean, error: unknown = null): void {
    if (this.disposed) return;
    this.activity = pending ? "CONNECTING" : null;
    this.error = error === null ? null : walletError(error, "CONNECTING");
    this.emit(false);
  }
  setExpectedSiweContext(context: { domain: string; uri: string; chainId?: number }): void {
    if (this.disposed) return;
    const chainId = context.chainId ?? this.expectedSourceChainId;
    if (!context.domain || !context.uri || !Number.isSafeInteger(chainId) || chainId <= 0) {
      throw new Error("Invalid SIWE configuration");
    }
    const changed = context.domain !== this.expectedSiweDomain || context.uri !== this.expectedSiweUri ||
      chainId !== this.expectedSourceChainId;
    const cancelSignIn = changed && this.activity === "SIGNING_IN";
    if (changed) this.siweRevision += 1;
    if (cancelSignIn) this.cancelAction();
    this.expectedSourceChainId = chainId;
    this.expectedSiweDomain = context.domain;
    this.expectedSiweUri = context.uri;
    if (changed) {
      this.invalidateCheck();
      this.checkRetainsAuthentication = false;
      if (!this.logoutBlocked) this.authState = "checking";
      this.emit(this.session !== null);
      this.recheckContext();
    }
  }

  clearExpectedSiweContext(): void {
    if (this.disposed) return;
    this.siweRevision += 1;
    this.expectedSiweDomain = null;
    this.expectedSiweUri = null;
    this.cancelAction();
    this.invalidateCheck();
    if (!this.logoutBlocked) this.authState = "configuration-error";
    this.emit(this.session !== null);
  }

  private recheckContext(): void {
    if (!this.logoutBlocked && this.expectedSiweDomain && this.expectedSiweUri) {
      void this.checkSession().catch(() => { /* Explicit retry, never a signature. */ });
    }
  }

  connect(provider = injectedProvider()): Promise<void> {
    if (this.disposed) return Promise.reject(new Error("Wallet controller has been disposed"));
    if (this.pending) return this.pending;
    if (this.connected && this.provider === provider && this.chainId !== null) return Promise.resolve();
    return this.runAction("CONNECTING", async (revision, signal) => {
      if (!provider) throw new Error("No injected wallet was found. Open or install a compatible wallet, then try again.");
      this.invalidateCheck();
      this.detach();
      this.identityRevision += 1;
      this.provider = provider;
      try {
        const account = await requestAccount(provider);
        signal.throwIfAborted();
        if (this.revision !== revision) throw new Error("Wallet context changed while connecting");
        this.account = account;
        this.attach();
        const chainId = await currentChainId(provider);
        signal.throwIfAborted();
        if (this.revision !== revision) throw new Error("Wallet context changed while connecting");
        this.account = account;
        this.chainId = chainId;
        this.error = null;

        this.emit();
        this.recheckContext();
      } catch (error) {
        if (this.revision === revision) {
          this.detach();
          this.provider = null;
          this.account = null;
          this.chainId = null;
        }
        throw error;
      }
    });
  }

  authenticate(): Promise<void> {
    if (this.disposed) return Promise.reject(new Error("Wallet controller has been disposed"));
    if (this.logoutBlocked) return Promise.reject(new Error("Logout is not complete. Retry logout before signing in."));
    if (this.pending) return this.activity === "CONNECTING" ? this.pending.then(() => this.authenticate()) : this.pending;
    return this.runAction("SIGNING_IN", async (_revision, signal) => {
      if (!this.provider || !this.account) throw new Error("Connect a wallet first");
      if (this.chainId !== this.expectedSourceChainId) throw new Error(`Switch to source chain ${this.expectedSourceChainId}`);
      if (!this.expectedSiweDomain || !this.expectedSiweUri) throw new Error("Sign-in configuration is unavailable. Retry loading it before signing in.");
      const provider = this.provider, account = this.account;
      const identity = this.identityRevision, siwe = this.siweRevision;
      let revision = this.revision;
      const assertContext = (acceptSessionCheck = false) => {
        signal.throwIfAborted();
        if (this.disposed || this.logoutBlocked || this.identityRevision !== identity || this.siweRevision !== siwe ||
          this.provider !== provider || this.account !== account || this.chainId !== this.expectedSourceChainId) {
          throw new Error("Wallet context changed during sign-in; sign in again");
        }
        if (!acceptSessionCheck && revision !== this.revision) throw new ApiSessionChangedError();
      };
      // Never ask for a signature until the cookie has actually been checked.
      await this.checkSession();
      assertContext(true);
      revision = this.revision;
      if (this.validSession()) return;
      if (this.authState !== "unauthenticated" && this.authState !== "waiting-wallet") {
        throw new Error(this.error ?? "Could not check the cookie session. Retry the session check.");
      }
      this.api.assertCookieCoordination();
      const issued = await this.api.post("/auth/nonce", { address: account }, nonceResponseSchema, signal);
      assertContext();
      if (issued.chainId !== this.expectedSourceChainId) throw new Error("Server issued a nonce for an unexpected chain");
      if (issued.domain !== this.expectedSiweDomain || issued.uri !== this.expectedSiweUri) throw new Error("Server issued a SIWE message for an unexpected domain or URI");
      const signature = await signSiweMessage(provider, account, issued.message);
      assertContext();
      this.signalChange();
      try {
        await this.api.cookieTransaction(async authApi => {
          assertContext();
          const verified = await authApi.post("/auth/verify-cookie", { message: issued.message, signature }, verifyResponseSchema, signal);
          assertContext();
          if (getAddress(verified.walletAddress) !== account) throw new Error("Authenticated wallet does not match the connected account");
          // Set-Cookie can be rejected by the browser: verify the round trip, chain,
          // identity and absolute deadline instead of trusting the verify response.
          await this.checkSession(true, authApi);
          assertContext(true);
          revision = this.revision;
          if (this.authState === "unavailable" || this.authState === "configuration-error") {
            throw new Error(this.error ?? "Could not confirm the cookie session. Retry the session check.");
          }
          if (this.sessionExpired) throw new Error("Session expired. Sign in again.");
          if (!this.validSession() || this.session?.authExpiresAt !== verified.expiresAt || this.session.csrfToken !== verified.csrfToken ||
            verified.issuedAt > this.session.serverTime || verified.expiresAt <= verified.issuedAt) {
            this.clearSession(false);
            this.authState = "configuration-error";
            this.emit();
            throw new Error("The browser did not confirm the expected cookie session. Check cookie configuration and retry.");
          }
        });
      } catch (error) {
        if (!signal.aborted) {
          if (error instanceof TypeError || error instanceof ApiError && error.status >= 500) this.authState = "unavailable";
          if (error instanceof ApiError && error.status === 403 && error.code.startsWith("CSRF_")) this.authState = "configuration-error";
          if (error instanceof ApiError && error.code === "COOKIE_AUTH_COORDINATION_UNAVAILABLE") this.authState = "configuration-error";
        }
        throw error;
      } finally {
        this.signalChange();
        // Any later restore/retry is ordered behind the actual mutation fetch.
        // Do not turn a failed verify into an automatic signing retry.
      }
    });
  }

  /** Public retry boundary; never signs and never replays a protected POST. */
  checkSession(force = false, authApi = this.api): Promise<void> {
    if (this.disposed || this.logoutBlocked) return Promise.resolve();
    if (!force && this.checking?.generation === this.checkGeneration) return this.checking.promise;
    if (force) this.invalidateCheck();
    // Expiry can invalidate checks itself. Capture the generation only after
    // that transition so this recheck owns the current context.
    const wasAuthenticated = this.validSession() !== null;
    const generation = this.checkGeneration;
    const abort = new AbortController();
    const started = this.now(), monotonic = performance.now();
    this.checkRetainsAuthentication = wasAuthenticated;
    this.authState = "checking";
    const current = () => !this.disposed && !this.logoutBlocked && generation === this.checkGeneration;
    const promise = authApi.get("/auth/session", sessionResponseSchema, abort.signal).then(response => {
      if (!current()) return;
      const previous = this.session;
      const sameSession = previous && previous.walletAddress === response.walletAddress && previous.chainId === response.chainId &&
        previous.authExpiresAt === response.authExpiresAt && previous.csrfToken === response.csrfToken;
      const previousNow = sameSession ? previous.authExpiresAt * 1_000 - this.remainingMs(previous) : 0;
      this.session = Object.freeze({ ...response, observedAt: this.now(), monotonicAt: performance.now(),
        requestDuration: Math.max(0, this.now() - started, performance.now() - monotonic, previousNow - response.serverTime * 1_000) });
      this.sessionExpired = this.remainingMs(this.session) <= 0;
      if (this.sessionExpired) {
        this.session = null;
        this.authState = "unauthenticated";
        this.error = "Session expired. Sign in again.";
      } else if (!this.expectedSiweDomain || !this.expectedSiweUri) {
        this.authState = "checking";
      } else if (response.chainId !== this.expectedSourceChainId) {
        this.authState = "configuration-error";
        this.error = "Cookie session chain does not match SIWE configuration. Retry after checking configuration.";
      } else {
        this.authState = this.matchesWallet(this.session) ? "authenticated" : "waiting-wallet";
        // Bridge can intentionally use another network with the same wallet.
        // WRONG_CHAIN still fences SIWE features; it does not require replacing the cookie.
        this.error = this.account && this.account !== this.session.walletAddress ? "The cookie belongs to another wallet. Sign in with the connected wallet to replace it." : null;
      }
      this.scheduleExpiry();
      this.emit((!sameSession && (previous !== null || this.session !== null)) || wasAuthenticated !== (this.authState === "authenticated"));
    }).catch((error: unknown) => {
      if (!current() || abort.signal.aborted) return;
      const hadSession = this.session !== null;
      if (error instanceof ApiError && error.status === 401) {
        this.session = null;
        this.authState = "unauthenticated";
        this.sessionExpired = hadSession || this.sessionExpired;
        this.error = this.sessionExpired ? "Session is no longer valid. Sign in again." : null;
      } else {
        this.authState = error instanceof ApiError && error.status === 403 ? "configuration-error" : "unavailable";
        this.error = this.authState === "configuration-error" ? "Cookie authentication was blocked by CSRF/origin policy. Check configuration and retry." :
          "Could not check the cookie session. Check your connection and retry; no new signature is required yet.";
      }
      this.emit(wasAuthenticated || hadSession && this.authState === "unauthenticated");
    }).finally(() => {
      if (this.checking?.promise === promise) { this.checking = null; this.emit(false); }
    });
    this.checking = { generation, promise, abort };
    this.emit(false);
    return promise;
  }

  private invalidateCheck(): void {
    ++this.checkGeneration;
    const checking = this.checking;
    this.checking = null;
    checking?.abort.abort();
  }

  /** Explicit user logout; passive disconnect/dispose never revoke the cookie. */
  logout(): Promise<void> {
    if (this.disposed) return Promise.reject(new Error("Wallet controller has been disposed"));
    if (this.logoutPending) return this.logoutPending;
    this.logoutBlocked = true;
    if (this.expiryTimer !== null) clearTimeout(this.expiryTimer);
    this.expiryTimer = null;
    this.cancelAction();
    this.invalidateCheck();
    this.authState = "logout-pending";
    this.error = null;
    const work = this.api.cookieTransaction(async authApi => {
      // Refresh CSRF under the shared cookie lock; a different tab may have
      // replaced the cookie. Retain metadata until server logout is confirmed.
      const response = await authApi.get("/auth/session", sessionResponseSchema);
      if (this.disposed) throw new Error("Wallet controller has been disposed");
      this.session = { ...response, observedAt: this.now(), monotonicAt: performance.now(), requestDuration: 0 };
      await authApi.logout();
    }).catch(async (error: unknown) => {
      if (error instanceof ApiError && error.status === 401) return; // No live server session remains.
      // A lost logout response is uncertain. Only a confirmed 401 resolves it.
      try { await this.api.get("/auth/session", sessionResponseSchema); }
      catch (checkError) { if (checkError instanceof ApiError && checkError.status === 401) return; }
      throw error;
    }).then(() => {
      if (this.disposed) return;
      this.session = null;
      this.logoutBlocked = false;
      this.authState = "unauthenticated";
      this.sessionExpired = false;
      this.error = null;
      this.emit(false);
    }).catch((error: unknown) => {
      if (!this.disposed) {
        this.authState = "logout-failed";
        this.error = error instanceof ApiError && error.status === 403 && error.code.startsWith("CSRF_") ?
          "Server logout was blocked by CSRF/origin policy. Check configuration and retry logout." :
          error instanceof ApiError && error.code === "COOKIE_AUTH_COORDINATION_UNAVAILABLE" ? error.message :
          "Server logout is not confirmed. Check your connection and retry logout to finish disconnecting.";
        this.emit(false);
      }
      throw error;
    }).finally(() => {
      if (this.logoutPending === work) this.logoutPending = null;
      this.signalChange();
    });
    this.logoutPending = work;
    this.emit();
    this.signalChange();
    return work;
  }

  private runAction(activity: "CONNECTING" | "SIGNING_IN",
    work: (revision: number, signal: AbortSignal) => Promise<void>): Promise<void> {
    // Pending actions are serialized separately. Starting/retrying a prompt is
    // presentation state; only an actual wallet/session change invalidates work.
    const revision = this.revision;
    const identity = this.identityRevision, siwe = this.siweRevision;
    const contextChanged = () => activity === "SIGNING_IN" ? this.identityRevision !== identity || this.siweRevision !== siwe : this.revision !== revision;
    const abort = new AbortController();
    this.actionAbort = abort;
    this.activity = activity;
    this.error = null;
    // Publish after assigning the shared promise so synchronous subscribers also
    // join the same request. Progress notifications do not change the context.
    const pending = abortable(() => Promise.resolve().then(() => {
      abort.signal.throwIfAborted();
      if (contextChanged()) throw new Error("Wallet context changed before the request started");
      return work(revision, abort.signal);
    }), abort.signal).catch((error: unknown) => {
      if (contextChanged() || abort.signal.aborted || error instanceof ApiSessionChangedError) throw error;
      if (error instanceof ApiError && error.code === "COOKIE_AUTH_COORDINATION_UNAVAILABLE") this.authState = "configuration-error";
      const message = walletError(error, activity);
      this.error = message;
      throw new Error(message);
    }).finally(() => {
      if (this.pending === pending) {
        this.pending = null;
        this.actionAbort = null;
        this.activity = null;
        this.emit(false);
      }
    });
    this.pending = pending;
    this.emit(false);
    return pending;
  }

  private cancelAction(): void {
    const abort = this.actionAbort;
    this.actionAbort = null;
    this.pending = null;
    this.activity = null;
    abort?.abort(new Error("Wallet context changed; try again with the current wallet"));
  }

  disconnect(): void {
    if (this.disposed) return;
    this.cancelAction();
    this.identityRevision += 1;
    this.detach();
    this.provider = null;
    this.connectionKey = null;
    this.account = null;
    this.chainId = null;
    this.invalidateCheck();
    if (!this.logoutBlocked) { this.session = null; this.error = null; this.authState = "unauthenticated"; }
    this.emit();
  }

  /** Release this instance without deleting the tab's recoverable login session. */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.invalidateCheck();
    this.channel?.close(); this.channel = null;
    window.removeEventListener("focus", this.onResume);
    window.removeEventListener("storage", this.onStorage);
    window.removeEventListener("pageshow", this.onResume);
    document.removeEventListener("visibilitychange", this.onResume);
    this.cancelAction();
    this.identityRevision++;
    this.detach();
    if (this.expiryTimer !== null) clearTimeout(this.expiryTimer);
    this.expiryTimer = null;
    this.session = null;
    this.authState = "unauthenticated";
    this.provider = null;
    this.account = null;
    this.chainId = null;
    this.activity = null;
    this.emit();
    this.listeners.clear();
  }

  clearSession(emit = true): void {
    if (this.disposed) return;
    if (emit) this.cancelAction();
    this.invalidateCheck();
    if (this.expiryTimer !== null) clearTimeout(this.expiryTimer);
    this.expiryTimer = null;
    this.session = null;
    this.authState = "unauthenticated";
    this.sessionExpired = false;
    if (emit) this.emit();
  }

  private remainingMs(session: CookieSession): number {
    // Wall elapsed catches OS sleep; monotonic elapsed catches wall-clock rollback.
    const elapsed = Math.max(0, this.now() - session.observedAt, performance.now() - session.monotonicAt);
    return session.authExpiresAt * 1_000 - session.serverTime * 1_000 - session.requestDuration - elapsed;
  }

  private matchesWallet(session: CookieSession): boolean {
    return this.account === session.walletAddress && this.chainId === this.expectedSourceChainId &&
      session.chainId === this.expectedSourceChainId;
  }

  private validSession(): CookieSession | null {
    if (this.authState === "checking" && !this.checkRetainsAuthentication) return null;
    if (this.logoutBlocked || !this.expectedSiweDomain || !this.expectedSiweUri ||
      !["authenticated", "checking", "waiting-wallet"].includes(this.authState)) return null;
    if (!this.session) return null;
    if (this.remainingMs(this.session) <= 0) {
      this.expireSession("Session expired. Sign in again.", false);
      return null;
    }
    return this.matchesWallet(this.session) ? this.session : null;
  }

  private expireSession(message: string, notify = true): void {
    this.cancelAction();
    this.clearSession(false);
    this.sessionExpired = true;
    this.error = message;
    if (notify) this.emit();
    else {
      this.revision += 1;
      this.snapshotCache = null;
      queueMicrotask(() => { if (!this.disposed) this.emit(false); });
    }
  }

  private scheduleExpiry(): void {
    if (this.expiryTimer !== null) clearTimeout(this.expiryTimer);
    this.expiryTimer = null;
    const session = this.session;
    if (!session || this.logoutBlocked) return;
    this.expiryTimer = setTimeout(() => {
      this.expiryTimer = null;
      if (this.session !== session) return;
      if (this.remainingMs(session) <= 0) this.expireSession("Session expired. Sign in again.");
      else this.scheduleExpiry();
    }, Math.min(2_147_483_647, Math.max(0, this.remainingMs(session))));
  }

  private readonly onAccountsChanged = (...args: unknown[]) => {
    const accounts = args[0];
    if (!Array.isArray(accounts) || typeof accounts[0] !== "string") return this.disconnect();
    const next = getAddress(accounts[0]);
    if (this.account === next) return;
    this.cancelAction();
    this.identityRevision += 1;
    this.error = null;
    this.invalidateCheck();
    if (!this.logoutBlocked) this.session = null;
    this.account = next;
    this.emit();
    this.recheckContext();
  };

  private readonly onChainChanged = (...args: unknown[]) => {
    const value = args[0];
    if (typeof value !== "string" || !/^0x[0-9a-f]+$/i.test(value)) return;
    if (this.chainId === Number(BigInt(value))) return;
    this.cancelAction();
    this.invalidateCheck();
    if (!this.logoutBlocked) this.session = null;
    this.chainId = Number(BigInt(value));
    this.error = null;
    this.emit();
    this.recheckContext();
  };

  private readonly onDisconnect = () => this.disconnect();

  private attach(): void {
    this.provider?.on?.("accountsChanged", this.onAccountsChanged);
    this.provider?.on?.("chainChanged", this.onChainChanged);
    this.provider?.on?.("disconnect", this.onDisconnect);
  }

  private detach(): void {
    this.provider?.removeListener?.("accountsChanged", this.onAccountsChanged);
    this.provider?.removeListener?.("chainChanged", this.onChainChanged);
    this.provider?.removeListener?.("disconnect", this.onDisconnect);
  }

  private emit(contextChanged = true): void {
    if (contextChanged) this.revision += 1;
    this.snapshotCache = null;
    for (const listener of this.listeners) listener();
  }
}

function walletError(error: unknown, activity: "CONNECTING" | "SIGNING_IN"): string {
  let current = error;
  for (let depth = 0; depth < 6 && current && typeof current === "object"; depth++) {
    const value = current as { code?: unknown; cause?: unknown };
    if (String(value.code) === "4001") return activity === "CONNECTING" ?
      "Wallet connection was rejected. You can try again." : "Sign-in was rejected in your wallet. You can try again.";
    if (String(value.code) === "-32002") return "A wallet request is already pending. Open your wallet to complete or reject it.";
    if (String(value.code) === "-32603") return "The wallet could not complete the request. Open your wallet and try again.";
    current = value.cause;
  }
  if (error instanceof ApiError) return error.status === 403 && error.code.startsWith("CSRF_") ?
    "Cookie authentication was blocked by CSRF/origin policy. Check configuration and retry." :
    error.code === "COOKIE_AUTH_COORDINATION_UNAVAILABLE" ? error.message :
    error.status === 401 ? "Sign-in was not accepted. Sign in again." :
    error.status === 429 ? "Too many sign-in attempts. Wait a moment before trying again." :
    "The sign-in service is unavailable or returned an invalid response. Try again.";
  if (error instanceof TypeError) return "Could not reach the sign-in service. Check your connection and try again.";
  return error instanceof Error ? error.message : "Wallet request failed. Try again.";
}
